import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Checkbox, Modal } from '../../../../components';
import { languageName } from '../../../../config/languageEndonyms';
import { GenerateButton } from '../../../blog-editor/components/GenerateButton';
import { defaultsTotal, hasDefaultsWork, type DefaultsPlan } from '../../defaults-plan';
import { defaultsFieldsCopy, defaultsPagesCopy } from '../../seo-copy';
import { Actions, Bar, Hint, Languages, Note, Row, RowHead } from './GenerateDefaults.styles';

export type GenerateDefaultsProps = {
  plan: DefaultsPlan;
  defaultLanguage: string;
  /** Credits per language, once the price table carries it (never zero-filled before that). */
  cost?: number;
  /** The bundle runs. */
  busy: boolean;
  /** Another generation runs. */
  disabled?: boolean;
  /** The checked languages, in plan order. */
  onGenerate: (languages: string[]) => void;
};

/** The languages with something to fill: the dialog's pre-checked set. */
const fillableOf = (plan: DefaultsPlan): Set<string> =>
  new Set(plan.languages.filter(hasDefaultsWork).map((p) => p.language));

/** No price, or nothing priced (only free title templates): no credits are shown. */
const shownCost = (total: number | undefined): number | undefined => (total === 0 ? undefined : total);

/**
 * "Generate site defaults": one Lumio call per language, for the site fields still empty. The button is priced for
 * every language with something to fill; the confirm dialog lets the owner drop languages, and says per language
 * which fields are written and how many pages inherit the new description. An empty title template is set by the page,
 * free and outside Lumio, so it is listed apart and never priced.
 */
export const GenerateDefaults = ({
  plan,
  defaultLanguage,
  cost,
  busy,
  disabled = false,
  onGenerate,
}: GenerateDefaultsProps): React.ReactElement => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<ReadonlySet<string>>(() => new Set());
  const nothing = plan.fillable === 0;
  const off = busy || disabled || nothing;

  const openDialog = (): void => {
    if (off) return;
    setSelected(fillableOf(plan));
    setOpen(true);
  };
  const close = (): void => setOpen(false);

  const toggle = (language: string, on: boolean): void => {
    setSelected((was) => {
      const next = new Set(was);
      if (on) next.add(language);
      else next.delete(language);
      return next;
    });
  };

  const chosen = plan.languages.filter((p) => hasDefaultsWork(p) && selected.has(p.language)).map((p) => p.language);
  const total = shownCost(defaultsTotal(plan, selected, cost));
  const confirmLabel =
    total === undefined
      ? t('Generate')
      : total === 1
        ? t('Generate (1 credit)')
        : t('Generate ({{total}} credits)', { total });

  const confirm = (): void => {
    if (off || chosen.length === 0) return;
    setOpen(false);
    onGenerate(chosen);
  };

  return (
    <Bar>
      <GenerateButton
        label="Generate site defaults"
        cost={nothing ? undefined : shownCost(defaultsTotal(plan, fillableOf(plan), cost))}
        disabled={off}
        onClick={openDialog}
      />
      {nothing ? <Hint text="Everything is already written. Clear a field to let Lumio write it." tag="span" /> : null}
      <Modal
        open={open}
        title={t('Generate site defaults with Lumio?')}
        onClose={close}
        footer={
          <Actions>
            <Button label={t('Cancel')} variant="outline" onClick={close} />
            <Button label={confirmLabel} disabled={off || chosen.length === 0} onClick={confirm} />
          </Actions>
        }
      >
        <Languages>
          {plan.languages.map((p) => {
            const empty = !hasDefaultsWork(p);
            const checked = !empty && selected.has(p.language);
            const pages = p.fields.includes('description')
              ? defaultsPagesCopy(t, p.pagesUsingDefault, p.pagesKeepingOwn)
              : '';
            const skipsAi =
              !empty &&
              !checked &&
              p.language === defaultLanguage &&
              (p.fields.includes('llms_summary') || p.fields.includes('llms_facts'));
            return (
              <Row key={p.language}>
                <RowHead>
                  {/* The endonym is shown as is in every UI language. */}
                  <Checkbox
                    checked={checked}
                    disabled={empty}
                    label={languageName(p.language)}
                    onChange={(on) => toggle(p.language, on)}
                  />
                  {empty ? <Hint text="Nothing to fill" tag="span" /> : null}
                </RowHead>
                {checked && p.fields.length > 0 ? <Note text={defaultsFieldsCopy(t, p.fields)} raw tag="span" /> : null}
                {checked && pages !== '' ? <Note text={pages} raw tag="span" /> : null}
                {checked && p.titleTemplate !== null ? (
                  <Note
                    text={t('Title template: {{template}}, set automatically, not written by Lumio', {
                      template: p.titleTemplate,
                    })}
                    raw
                    tag="span"
                  />
                ) : null}
                {skipsAi ? (
                  <Note
                    text="The AI assistant texts are written only with this language, so they are skipped."
                    tag="span"
                  />
                ) : null}
              </Row>
            );
          })}
        </Languages>
      </Modal>
    </Bar>
  );
};

export default GenerateDefaults;
